import React, { useState, useEffect } from 'react';
import { Modal, Button } from '@/components/ui';
import { IndianRupee } from 'lucide-react';
import toast from 'react-hot-toast';

export const RecordSaleReturnModal = ({ isOpen, onClose, sale, onReturnSuccess }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");
  const [refundMode, setRefundMode] = useState("bank");

  useEffect(() => {
    if (isOpen && sale) {
      setItems((sale.items || sale.rows || []).map(it => ({
        ...it,
        returnQty: 0
      })));
      setReason("");
    }
  }, [isOpen, sale]);

  const handleQtyChange = (index, val) => {
    const updated = [...items];
    updated[index].returnQty = Math.max(0, Number(val));
    setItems(updated);
  };

  const handleDamagedChange = (index, checked) => {
    const updated = [...items];
    updated[index].isDamaged = checked;
    setItems(updated);
  };

  const calculateTotalRefund = () => {
    return items.reduce((sum, item) => sum + ((Number(item.returnQty) || 0) * (Number(item.rate || item.unitPrice || 0))), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const returnItems = items.filter(it => it.returnQty > 0);
    if (returnItems.length === 0) {
      toast.error("Please specify a return quantity for at least one item.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        returnType: "SALE",
        saleId: sale._id,
        docType: sale.docType || (sale.invoiceNo ? 'Order' : 'Direct Sale'),
        saleType: sale.saleType || (sale.project ? 'Project Sale' : 'Direct Sale'),
        returnNo: `RTN-${Date.now().toString().slice(-6)}`,
        returnDate: new Date(),
        reason,
        refundMode,
        totalAmount: calculateTotalRefund(),
        items: returnItems.map(it => {
          const invId = it.inventoryId || it.item?._id || it.item;
          const mappedItem = {
            itemName: it.itemName || it.description || it.particulars || 'Unknown Item',
            quantity: it.returnQty,
            rate: it.rate || it.unitPrice || (it.amount ? it.amount / (it.quantity || 1) : 0),
            reason,
            isDamaged: it.isDamaged || false
          };
          if (invId) mappedItem.inventoryId = invId;
          return mappedItem;
        })
      };

      const { default: API } = await import('@/lib/API');
      const response = await API.post('/final-inventory/material-returns', payload);
      
      toast.success("Sale return recorded successfully!");
      if (onReturnSuccess) onReturnSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || err.message || 'Failed to record return');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !sale) return null;

  const totalRefund = calculateTotalRefund();

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Record Sale Return" className="max-w-3xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-4">
          <p className="text-sm text-blue-800 font-medium">Original Sale Details</p>
          <div className="grid grid-cols-2 gap-4 mt-2 text-sm text-blue-900">
            <div><strong>Sale ID:</strong> {sale.invoiceNo || sale.quotationNo || sale._id}</div>
            <div><strong>Customer:</strong> {sale.contactDetails?.name || sale.party || 'N/A'}</div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-slate-500 uppercase bg-slate-50">
              <tr>
                <th className="px-4 py-2">Item Name</th>
                <th className="px-4 py-2 text-right">Sold Qty</th>
                <th className="px-4 py-2 text-right">Rate</th>
                <th className="px-4 py-2 text-right">Return Qty</th>
                <th className="px-4 py-2 text-center">Damaged Item</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const maxQty = Number(item.quantity || item.qty || 0);
                return (
                  <tr key={idx} className="border-b">
                    <td className="px-4 py-2">{item.itemName || item.description || item.particulars || 'Unknown'}</td>
                    <td className="px-4 py-2 text-right">{maxQty}</td>
                    <td className="px-4 py-2 text-right">₹{item.rate || item.unitPrice || (item.amount ? (item.amount / maxQty).toFixed(2) : 0)}</td>
                    <td className="px-4 py-2 text-right">
                      <input
                        type="number"
                        min="0"
                        max={maxQty}
                        value={item.returnQty || 0}
                        onChange={(e) => handleQtyChange(idx, e.target.value)}
                        className="w-20 border rounded px-2 py-1 text-right focus:outline-none focus:border-blue-500 inline-block"
                      />
                    </td>
                    <td className="px-4 py-2 text-center align-middle">
                      <input
                        type="checkbox"
                        checked={item.isDamaged || false}
                        onChange={(e) => handleDamagedChange(idx, e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500 cursor-pointer"
                        title="Mark as damaged stock"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Return Reason</label>
            <textarea
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full border rounded-lg p-2 min-h-[80px]"
              placeholder="Why are these items being returned?"
            />
          </div>
          <div className="flex flex-col justify-end items-end space-y-2 pb-2">
             <div className="text-sm text-slate-500 font-medium">Estimated Refund</div>
             <div className="text-2xl font-bold text-[#f97316] flex items-center">
                <IndianRupee size={20} className="mr-1" /> {totalRefund.toFixed(2)}
             </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" type="button" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" loading={loading} disabled={loading || totalRefund <= 0}>
            Record Return
          </Button>
        </div>
      </form>
    </Modal>
  );
};
